// generated by the plop

import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { PROGRESS_STATUSES } from 'symbols';
import { TelegramChatService } from '../../service/telegram-chat.service';
import {
  GetChatList,
  GetChatListFail,
  GetChatListSuccess,
  GetChatMessages,
  GetChatMessagesFail,
  GetChatMessagesSuccess,
  GetUnreadMessagesCount,
  GetUnreadMessagesSuccess,
  NewMessageWS,
  ReadMessageByChatId,
  SendMessageToChat,
  SendMessageToChatSuccess
} from './telegram-chat.actions';
import { TELEGRAM_CHAT_STATE_DEFAULT, TelegramChatStateModel } from './telegram-chat.model';

@State({ name: 'TelegramChat', defaults: TELEGRAM_CHAT_STATE_DEFAULT })
@Injectable()
export class TelegramChatState {

  constructor(private telegramChatService: TelegramChatService, private store: Store) {
  }

  @Selector()
  static messages(state: TelegramChatStateModel): TelegramChatStateModel['messages'] {
    return state.messages;
  }

  @Selector()
  static unreadMessagesCount(state: TelegramChatStateModel): TelegramChatStateModel['unreadMessagesCount'] {
    return state.unreadMessagesCount;
  }

  @Selector()
  static loadChatListStatus(state: TelegramChatStateModel): TelegramChatStateModel['loadChatListStatus'] {
    return state.loadChatListStatus;
  }

  @Selector()
  static chatList(state: TelegramChatStateModel): TelegramChatStateModel['chatList'] {
    return state.chatList;
  }

  @Action(GetChatList)
  getChatList(ctx: StateContext<TelegramChatStateModel>, { filter, lazy }: GetChatList): void {
    if (lazy) {
      const next = ctx.getState().chatList?.links?.next;
      if (next) {
        ctx.patchState({
          loadChatListStatus: PROGRESS_STATUSES.IN_PROGRESS
        });
        this.telegramChatService.getChatListLazy(next).subscribe({
          next: res => ctx.dispatch(new GetChatListSuccess(res, lazy)),
          error: error => ctx.dispatch(new GetChatListFail(error))
        });
      }
    } else {
      ctx.patchState({
        loadChatListStatus: PROGRESS_STATUSES.IN_PROGRESS
      });
      this.telegramChatService.getChatList(filter).subscribe({
        next: res => ctx.dispatch(new GetChatListSuccess(res, lazy)),
        error: error => ctx.dispatch(new GetChatListFail(error))
      });
    }
  }

  @Action(GetChatListSuccess)
  getChatListSuccess(ctx: StateContext<TelegramChatStateModel>, { chatList, lazy }: GetChatListSuccess): void {
    ctx.patchState({
      loadChatListStatus: PROGRESS_STATUSES.SUCCESS
    });
    if (lazy) {
      ctx.setState(patch({
        chatList: patch({
          data: append([...chatList.data]),
          links: chatList.links,
          meta: chatList.meta
        })
      }));
    } else {
      ctx.patchState({
        chatList
      });

    }
  }

  @Action(GetChatListFail)
  getChatListFail(ctx: StateContext<TelegramChatStateModel>, { error }: GetChatListFail): void {
    ctx.patchState({
      loadChatListStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(GetUnreadMessagesCount)
  getUnreadMessagesCount(ctx: StateContext<TelegramChatStateModel>): void {
    this.telegramChatService.getUnreadMessagesCount().subscribe();
  }


  @Action(GetUnreadMessagesSuccess)
  getUnreadMessagesSuccess(ctx: StateContext<TelegramChatStateModel>, { count }: GetUnreadMessagesSuccess): void {
    ctx.patchState({
      unreadMessagesCount: count
    });
  }

  @Action(GetChatMessages)
  getChatMessages(ctx: StateContext<TelegramChatStateModel>, { chatId, filter, lazy }: GetChatMessages): void {
    if (lazy) {
      const next = ctx.getState().messages[chatId]?.links?.next;
      if (next) {
        this.telegramChatService.getChatMessageListLazy(next).subscribe({
          next: res => ctx.dispatch(new GetChatMessagesSuccess(chatId, res, lazy)),
          error: error => ctx.dispatch(new GetChatListFail(error))
        });
      }
    } else {
      this.telegramChatService.getMessagesByChatId(chatId, filter).subscribe({
        next: res => ctx.dispatch(new GetChatMessagesSuccess(chatId, res, lazy)),
        error: error => ctx.dispatch(new GetChatListFail(error))
      });
    }
  }

  @Action(GetChatMessagesSuccess)
  getChatMessagesSuccess(ctx: StateContext<TelegramChatStateModel>, {
    chatId,
    messages,
    lazy
  }: GetChatMessagesSuccess): void {
    if (lazy) {
      const state = ctx.getState();
      ctx.patchState({
        messages: {
          ...state.messages, [chatId]: {
            data: [...messages.data.reverse(), ...state.messages[chatId].data],
            links: messages.links,
            meta: messages.meta
          }
        }
      });

    } else {
      ctx.setState(patch({
        messages: patch({
          [chatId]: {
            links: messages.links,
            meta: messages.meta,
            data: messages.data.reverse()
          }
        })
      }));
    }
  }

  @Action(GetChatMessagesFail)
  getChatMessagesFail(ctx: StateContext<TelegramChatStateModel>, { error }:
  GetChatMessagesFail): void {
    // Handle error if needed
    console.error('Failed to get chat messages:', error);
  }

  @Action(NewMessageWS)
  newMessageWS(ctx: StateContext<TelegramChatStateModel>, { payload }: NewMessageWS) {
    const state = ctx.getState();

    // чат із новим повідомленням (від клієнта чи менеджера) піднімаємо нагору списку з новим прев'ю
    const chats = state.chatList?.data;
    const index = chats?.findIndex(chat => chat.id === payload.chatId) ?? -1;
    if (index >= 0) {
      const updated = { ...chats[index], lastMessage: payload };
      ctx.patchState({
        chatList: { ...state.chatList, data: [updated, ...chats.filter((_, i) => i !== index)] }
      });
    }

    if (state.messages?.[payload.chatId]) {
      ctx.patchState({
        messages: {
          ...state.messages,
          [payload.chatId]: {
            ...state.messages[payload.chatId],
            data: [...state.messages[payload.chatId].data, payload]
          }
        }
      });
    }
  }

  @Action(ReadMessageByChatId)
  readMessageById(ctx: StateContext<TelegramChatStateModel>, { chatId }: ReadMessageByChatId) {
    this.telegramChatService.readMessageByChatId(chatId).subscribe({});
  }

  @Action(SendMessageToChat)
  sendMessageToChat(ctx: StateContext<TelegramChatStateModel>, { message, chatId }: SendMessageToChat) {
    this.telegramChatService.sendMessageToChat(chatId, message).subscribe({
      next: () => ctx.dispatch(new SendMessageToChatSuccess())
    });
  }
}


